/** @userstory US-01 US-05 US-06 US-34 US-35 */
import apiClient from './apiClient';

// Interface for fetching freelance profile
export interface GetFreelanceProfile {
	id: string;
	profileQualityScore?: number;
	profileQualityActions?: string[];
	talentId?: string;
	firstName: string;
	lastName: string;
	email: string;
	hourlyRate: number;
	employmentTypes: string[];
	openToWork: boolean;
	minAnnualGrossSalary?: number | null;
	availableFrom: string;
	isVisibleOnline: boolean;
	location: string;
	currentTitle: string;
	bioHtml: string;
	whatLookingFor: string;
	personalStatement: string;
	skills: string[];
	tags: string[];
	legalName: string;
	vatNumber: string;
	fiscalCode: string;
	italianSdiCode: string;
	billingAddress: string;
	billingCity: string;
	billingProvince: string;
	billingZipCode: string;
	billingCountryCode: string;
	/** Business type: "Person" (sole proprietor) or "Company" (corporation) — US-07 */
	businessType?: string;
	/** Owner's personal fiscal code, distinct from company FiscalCode — US-24 */
	billingFiscalCode?: string;
	/** VAT percentage applied on invoices (0-100) — US-12 */
	vatPercentage?: number;
	billingEmail: string;
	certifiedEmail: string;
	iban: string;
	phoneNumber?: string;
	// Media fields
	profilePictureUrl?: string;
	profilePictureThumbnailUrl?: string;
	introductionVideoUrl?: string;
	introductionVideoThumbnailUrl?: string;
	introductionVideoDurationSeconds?: number;
	// Storage fields
	usedStorageBytes?: number;
	maxStorageBytes?: number;
}

// Interface for updating freelance profile (make fields optional to support partial updates)
export interface UpdateFreelanceProfile {
	firstName?: string;
	lastName?: string;
	email?: string;
	phoneNumber?: string;
	currentTitle?: string;
	hourlyRate?: number;
	employmentTypes?: string[];
	openToWork?: boolean;
	minAnnualGrossSalary?: number | null;
	availableFrom?: string;
	isVisibleOnline?: boolean;
	location?: string;
	bioHtml?: string;
	skills?: string[];
	tags?: string[];
	whatLookingFor?: string;
	personalStatement?: string;
	legalName?: string;
	vatNumber?: string;
	fiscalCode?: string;
	italianSdiCode?: string;
	vatPercentage?: number;
	billingAddress?: string;
	billingCity?: string;
	billingProvince?: string;
	billingZipCode?: string;
	billingCountryCode?: string;
	/** Business type: "Person" or "Company" — US-07 */
	businessType?: string;
	/** Owner's personal fiscal code — US-24 */
	billingFiscalCode?: string;
	billingEmail?: string;
	certifiedEmail?: string;
	iban?: string;
}

const profileService = {
	/**
	 * Fetches the freelance user profile from the backend
	 * @userstory US-01
	 */
	async getFreelanceProfile(): Promise<GetFreelanceProfile> {
		try {
			const response = await apiClient.get<GetFreelanceProfile>('/api/talentusers/profile');
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to fetch profile data';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Updates the freelance user profile
	 * @userstory US-05 US-06
	 */
	async updateFreelanceProfile(payload: UpdateFreelanceProfile): Promise<GetFreelanceProfile> {
		try {
			const response = await apiClient.put<GetFreelanceProfile>(
				'/api/talentusers/profile',
				payload,
			);
			return response.data;
		} catch (error: any) {
			const errorMessage = error.response?.data?.message || 'Failed to update profile';
			throw new Error(errorMessage);
		}
	},

	/**
	 * Uploads a CV to the freelance user profile
	 * This operation includes AI-based CV parsing and profile indexing which can take several minutes
	 * @userstory US-34 US-35
	 */
	/**
	 * Uploads the CV and returns as soon as the server accepts it (HTTP 202). The slow AI parse runs
	 * in the background — poll {@link getCvStatus} to know when the profile is ready. Use this for the
	 * apply flow so the user perceives ≤10s.
	 * @userstory US-OPEN-09
	 */
	async startCvUpload(file: File): Promise<GetFreelanceProfile> {
		try {
			const formData = new FormData();
			formData.append('file', file);
			const response = await apiClient.post<GetFreelanceProfile>(
				'/api/talentusers/profile/cv',
				formData,
				{
					headers: { 'Content-Type': 'multipart/form-data' },
					timeout: 300000,
					// The CV upload is a long, non-idempotent multipart POST — never auto-retry it.
					'axios-retry': { retries: 0 },
				},
			);
			return response.data;
		} catch (error: any) {
			throw new Error(error.response?.data?.message || 'Failed to upload CV');
		}
	},

	/**
	 * Polls the async CV processing status while the background worker parses the CV.
	 * @userstory US-OPEN-09
	 */
	async getCvStatus(): Promise<{ status: string; profile: GetFreelanceProfile | null }> {
		const response = await apiClient.get<{
			status: string;
			profile: GetFreelanceProfile | null;
		}>('/api/talentusers/profile/cv/status');
		return response.data;
	},

	/**
	 * Uploads the CV and waits (by polling) until the profile is built, returning it. Backward
	 * compatible: if the server responds 200 with a profile (mock/legacy), it is returned directly.
	 * Used by onboarding and settings where the parsed profile is reviewed immediately.
	 * @userstory US-34 US-35
	 */
	async uploadCV(file: File): Promise<GetFreelanceProfile> {
		try {
			const formData = new FormData();
			formData.append('file', file);
			const response = await apiClient.post<GetFreelanceProfile>(
				'/api/talentusers/profile/cv',
				formData,
				{
					headers: { 'Content-Type': 'multipart/form-data' },
					// Extended timeout for the (possibly slow) upload; the parse itself is async.
					timeout: 300000,
					// Opt OUT of the global axios-retry: a non-idempotent multipart POST must never be replayed.
					'axios-retry': { retries: 0 },
				},
			);
			// Legacy / mocked path: a 200 already contains the parsed profile.
			if (response.status === 200) {
				return response.data;
			}
			// Async path (202 Accepted): poll until the background parse completes.
			const POLL_INTERVAL_MS = 2500;
			const POLL_MAX_MS = 150000;
			const start = Date.now();
			while (Date.now() - start < POLL_MAX_MS) {
				await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
				const { status, profile } = await profileService.getCvStatus();
				if (status === 'Done' && profile) return profile;
				if (status === 'Failed') throw new Error('CV processing failed');
			}
			throw new Error('CV processing timed out');
		} catch (error: any) {
			throw new Error(
				error.response?.data?.message || error.message || 'Failed to upload CV',
			);
		}
	},
};

export default profileService;
